import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import DeleteModal from "components/common/DeleteModal";
import { IndexColumn } from "components/common/columns";
import { getDocumentTitle } from "utils/helpers";
import { toast } from "react-toastify";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import type { Department } from "../types/department.types";
import { deleteDepartment, getDepartments } from "../services/DepartmentService";
import { DepartmentModal } from "./DepartmentModal";

const DepartmentList = () => {
  document.title = getDocumentTitle("Departments");
  const { can } = useAccess();
  const canCreate = can(Permissions.DEPARTMENT.CREATE);
  const canUpdate = can(Permissions.DEPARTMENT.UPDATE);
  const canDelete = can(Permissions.DEPARTMENT.DELETE);

  const tableRef = useRef<() => void>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<Department | null>(null);

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row: Department) => {
    setEditing(row);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const columns: ColumnDef<Department>[] = useMemo(
    () => [
      IndexColumn<Department>(),
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="fw-medium">{row.original.name}</span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const hasMenu = canUpdate || canDelete;
          if (!hasMenu) return <span className="text-muted small">—</span>;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Department actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canUpdate ? (
                  <DropdownItem onClick={() => openEdit(r)}>
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canDelete ? (
                  <>
                    {canUpdate ? <DropdownItem divider /> : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => {
                        setSelected(r);
                        setDeleteOpen(true);
                      }}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canDelete, canUpdate, openEdit],
  );

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await deleteDepartment(selected.id);
      if (res.success) {
        toast.success(res.message);
        tableRef.current?.();
      }
      setDeleteOpen(false);
      setSelected(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? err?.message ?? "Delete failed",
      );
    }
  };

  return (
    <React.Fragment>
      <DepartmentModal
        show={modalOpen}
        editing={editing}
        onClose={closeModal}
        onSuccess={() => tableRef.current?.()}
      />
      <DeleteModal
        show={deleteOpen}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        recordId={selected?.name}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Departments" pageTitle="HR Management" />
          <Card>
            <CardHeader className="d-flex flex-wrap mb-3 justify-content-between align-items-center gap-2 border-bottom">
              <div>
                <h5 className="card-title mb-1">Departments</h5>
                <p className="text-muted small mb-0">
                  Manage departments used to categorize employees.
                </p>
              </div>
              {canCreate ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openCreate}
                >
                  <i className="ri-add-line align-bottom me-1" />
                  Add department
                </button>
              ) : null}
            </CardHeader>
            <div className="card-body pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getDepartments}
                pageSize={10}
                globalSearch={true}
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-0"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
              />
            </div>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DepartmentList;
